import { BADGE_DEFINITIONS, getBadgeById, hasBadge } from './badgeDefinitions';
import ProgressService from '../services/ProgressService';

/**
 * Badge Service - Handles badge checking and awarding logic
 */
class BadgeService {
  /**
   * Check and award badges based on user progress
   * @param {Object} progress - User progress object
   * @param {Object} context - Additional context (e.g., quiz score, lesson completed)
   * @returns {Array} Array of newly awarded badges
   */
  static async checkAndAwardBadges(progress, context = {}) {
    const newlyAwarded = [];
    const currentBadges = progress.badges || [];
    
    // Check streak badges
    if (progress.streak) {
      const streakBadges = this.checkStreakBadges(progress.streak, currentBadges);
      newlyAwarded.push(...streakBadges);
    }
    
    // Check level badges
    if (progress.level) {
      const levelBadges = this.checkLevelBadges(progress.level, currentBadges);
      newlyAwarded.push(...levelBadges);
    }
    
    // Check lesson badges
    if (progress.completedLessons) {
      const lessonCount = Array.isArray(progress.completedLessons) 
        ? progress.completedLessons.length 
        : 0;
      const lessonBadges = this.checkLessonBadges(lessonCount, currentBadges);
      newlyAwarded.push(...lessonBadges);
    }
    
    // Check XP badges
    if (progress.xp) {
      const xpBadges = this.checkXpBadges(progress.xp, currentBadges);
      newlyAwarded.push(...xpBadges);
    }
    
    // Check fact badges
    if (progress.completedFacts) {
      const factCount = Array.isArray(progress.completedFacts) 
        ? progress.completedFacts.length 
        : 0;
      const factBadges = this.checkFactBadges(factCount, currentBadges);
      newlyAwarded.push(...factBadges);
    }
    
    // Check quiz badges (from context)
    if (context.quizCompleted) {
      const quizBadges = this.checkQuizBadges(context, currentBadges);
      newlyAwarded.push(...quizBadges);
    }
    
    // Award badges
    for (const badge of newlyAwarded) {
      try {
        await ProgressService.addBadge(badge);
      } catch (error) {
        console.error(`Failed to award badge ${badge.id}:`, error);
      }
    }
    
    return newlyAwarded;
  }
  
  /**
   * Check streak badges
   */
  static checkStreakBadges(streak, currentBadges) {
    const awarded = [];
    const streakMilestones = [3, 7, 14, 30, 60, 100];
    
    for (const milestone of streakMilestones) {
      if (streak >= milestone) {
        const badgeId = `streak-${milestone}`;
        if (!hasBadge(currentBadges, badgeId)) {
          const badge = getBadgeById(badgeId);
          if (badge) {
            awarded.push(badge);
          }
        }
      }
    }
    
    return awarded;
  }
  
  /**
   * Check level badges
   */
  static checkLevelBadges(level, currentBadges) {
    const awarded = [];
    const levelMilestones = [5, 10, 20, 30, 50];
    
    for (const milestone of levelMilestones) {
      if (level >= milestone) {
        const badgeId = `level-${milestone}`;
        if (!hasBadge(currentBadges, badgeId)) {
          const badge = getBadgeById(badgeId);
          if (badge) {
            awarded.push(badge);
          }
        }
      }
    }
    
    return awarded;
  }
  
  /**
   * Check lesson badges
   */
  static checkLessonBadges(lessonCount, currentBadges) {
    const awarded = [];
    const lessonMilestones = [1, 5, 10, 25, 50];
    
    for (const milestone of lessonMilestones) {
      if (lessonCount >= milestone) {
        const badgeId = `lesson-${milestone}`;
        if (!hasBadge(currentBadges, badgeId)) {
          const badge = getBadgeById(badgeId);
          if (badge) {
            awarded.push(badge);
          }
        }
      }
    }
    
    return awarded;
  }
  
  /**
   * Check XP badges
   */
  static checkXpBadges(xp, currentBadges) {
    const awarded = [];
    const xpMilestones = [1000, 5000, 10000, 25000, 50000];
    
    for (const milestone of xpMilestones) {
      if (xp >= milestone) {
        const badgeId = `xp-${milestone}`;
        if (!hasBadge(currentBadges, badgeId)) {
          const badge = getBadgeById(badgeId);
          if (badge) {
            awarded.push(badge);
          }
        }
      }
    }
    
    return awarded;
  }
  
  /**
   * Check fact badges
   */
  static checkFactBadges(factCount, currentBadges) {
    const awarded = [];
    const factMilestones = [10, 25, 50, 100];
    
    for (const milestone of factMilestones) {
      if (factCount >= milestone) {
        const badgeId = `fact-${milestone}`;
        if (!hasBadge(currentBadges, badgeId)) {
          const badge = getBadgeById(badgeId);
          if (badge) {
            awarded.push(badge);
          }
        }
      }
    }
    
    return awarded;
  }
  
  /**
   * Check quiz badges
   */
  static checkQuizBadges(context, currentBadges) {
    const awarded = [];
    
    // Check for perfect score
    if (context.quizScore === 100 && !hasBadge(currentBadges, 'quiz-perfect')) {
      const badge = getBadgeById('quiz-perfect');
      if (badge) {
        awarded.push(badge);
      }
    }
    
    // Check for mastery (95%+)
    if (context.quizScore >= 95 && !hasBadge(currentBadges, 'quiz-mastery')) {
      const badge = getBadgeById('quiz-mastery');
      if (badge) {
        awarded.push(badge);
      }
    }
    
    // Check quiz count badges (this would need quiz count from progress)
    // For now, we'll handle this separately when we have quiz count data
    
    return awarded;
  }
  
  /**
   * Get user's badge progress for a specific badge
   */
  static getBadgeProgress(badgeId, progress) {
    const badge = getBadgeById(badgeId);
    if (!badge) return null;
    
    const requirement = badge.requirement;
    let current = 0;
    let target = requirement.value;
    
    switch (requirement.type) {
      case 'streak':
        current = progress.streak || 0;
        break;
      case 'level':
        current = progress.level || 0;
        break;
      case 'lessons':
        current = Array.isArray(progress.completedLessons) 
          ? progress.completedLessons.length 
          : 0;
        break;
      case 'xp':
        current = progress.xp || 0;
        break;
      case 'facts':
        current = Array.isArray(progress.completedFacts) 
          ? progress.completedFacts.length 
          : 0;
        break;
      default:
        return null;
    }
    
    return {
      current,
      target,
      progress: Math.min((current / target) * 100, 100),
      completed: current >= target
    };
  }
}

export default BadgeService;


